import Axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { apiRequest, apiRequestCustomConfig, onApiCatchError } from '../ApiRequestHelper';
import auth from '../../../auth/authService';
import {
  ChangeJigOwnerResult,
  DynaconfConfig,
  PricignListItem,
  PricingTier,
  HTTPAction,
  LibraryCategory,
  LibraryEntry,
  ChangeJigOwnerRequest,
  ChangeOwnerTenantRequest,
  MyJig,
  JigMetaData,
  JigShareRequest,
  JigShareURLData,
  JigVisibilities,
  UploadThumbnailRequest,
  QueueIdResult,
  JigPermissionDeleteRequest,
  JigPermissionCreateRequest,
  JigPermissionUpdateRequest,
  TenantCreateRequest,
  Tenant,
  TenantToken,
  GetTenantsByUserRequest,
  TenantUserAddRequest,
  TenantUserRemoveRequest,
  TenantUserUpdateRequest,
  ReportRequest, UploadTenantLogoRequest, TenantLogoImageResponse, TenantSearchResponse,
} from './types';
import * as pipeline from './pipeline';
import { TenantHelpers } from '@/utils/tenant-helpers';
import * as AppConst from '../Constants';
import { Report } from '@/store/modules/reports';
import { loadingModalMutationName, nonSystemErrors } from '../Constants';
import { CreateCheckoutRequest, CreateCustomerPortalRequest } from '@/modules/subscription/types';
import { AppHelper } from '@/utils/app-helper'
declare var consoleLog: any;
declare var consoleError: any;

const CancelToken = Axios.CancelToken;
let myJigsListCall: any = null;
let teamJigsListCall: any = null;

export default {
  setUid(context: any, uid: number) {
    context.commit('updateUid', uid);
  },
  executeStatisticReport(context: any, reportRequest: ReportRequest): Promise<boolean> {
    let urlParams = {};
    let myMutation = '';
    const tenantId = getMyTenantIDFromContext(context);

    switch (reportRequest.Name) {
      case Report.TenantDashboard:
        myMutation = 'updateJigViewGraph';
        urlParams = {
          name: reportRequest.Name,
          jig_hash_id: reportRequest.JigHashId,
          start_date: reportRequest.StartDate,
          end_date: reportRequest.EndDate,
          comparePreviousPeriod: reportRequest.ComparePreviousPeriod,
        };
        break;
      case Report.JigViewActivity:
        myMutation = 'updateJigViewGraph';
        urlParams = {
          name: reportRequest.Name,
          jig_hash_id: reportRequest.JigHashId,
          start_date: reportRequest.StartDate,
          end_date: reportRequest.EndDate,
          comparePreviousPeriod: reportRequest.ComparePreviousPeriod,
        };
        break;
      case Report.MostPopularJig:
        myMutation = 'updateMostPopularJigGraph';
        urlParams = {
          name: reportRequest.Name,
          top: reportRequest.Limit,
          reportDays: reportRequest.LookBackDays,
          comparePreviousPeriod: reportRequest.ComparePreviousPeriod,
        };
        break;
      case Report.AppActivities:
        myMutation = 'updateAppActivitiesGraph';
        urlParams = {
          name: reportRequest.Name,
          // some other query params...
        };
        break;
      default:
        myMutation = 'mutation' + reportRequest.Name;
    }

    return apiRequest(
      {
        action: HTTPAction.GET,
        url: `${AppConst.statisticsUrl}`,
        params: urlParams,
      },
      context,
      myMutation,
      `executing report ${reportRequest.Name} using mutation ${myMutation}`,
      AppConst.isStatisticsLoadingMutationName,
      {
        [AppConst.CustomHeaderKeyTenantId]: tenantId,
      }
    );
  },

  async loadMyJigs(context: any): Promise<boolean> {
    myJigsListCall = CancelToken.source();

    /**
     * As My Jigs page and Team Jigs page are using the same view template,
     * if route changes while GET request is still ongoing plus the Jigs list is reasonable long,
     * it will overwrite the result of new GET request call on new route.
     * Or there will be a noticeable time delay before new list appears.
     * So we need to cancel any ongoing call to avoid situation like Team Jigs are showing on My Jigs page
     * or My Jigs showing on Team Jigs page.
     */
    if (teamJigsListCall) {
      teamJigsListCall.cancel('Aborting request to get Team Jigs before get My Jigs request.');
    }

    const urlParams = {
      editable: 'false',
    };

    const result = await apiRequest({
      action: HTTPAction.GET,
      url: AppConst.myJigsV2Url,
      params: urlParams,
      cancelToken: myJigsListCall.token,
    }, context, 'updateMyJigs', `getting my jigs`);

    myJigsListCall = null;

    return result;
  },

  async loadTeamJigs(context: any): Promise<boolean> {
    teamJigsListCall = CancelToken.source();

    if (myJigsListCall) {
      myJigsListCall.cancel('Aborting request to get My Jigs before get Team Jigs request.');
    }

    const urlParams = {
      editable: 'false',
    };
    const tenantId = getMyTenantIDFromContext(context);

    const result = await apiRequest(
      {
        action: HTTPAction.GET,
        url: `${AppConst.tenantsV1Url}/${tenantId}${AppConst.tenantsJigsUrl}`,
        params: urlParams,
        cancelToken: teamJigsListCall.token,
      },
      context,
      'UpdateTeamJigs',
      `getting team jigs`,
      AppConst.loadingMutationName,
      {
        [AppConst.CustomHeaderKeyTenantId]: tenantId,
      }
    );

    teamJigsListCall = null;

    return result;
  },

  getLatestDesktopVersion(context: any): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.GET,
      url: `${AppConst.versionUrl}/jigspace/desktop`,
    }, context, 'updateLatestDesktopVersion', `getting latest desktop version`);
  },

  loadMyJigsWithMetrics(context: any): Promise<boolean> {
    const urlParams = {
      editable: 'false',
    };
    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.myJigsV2UrlMetrics,
      params: urlParams,
    }, context, 'updateMyJigs', `getting my jigs`);
  },

  loadJigMetadata(context: any, payload: any): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.GET,
      url: `${AppConst.jigsV2Url}/${payload.jigId}/${AppConst.jigMetadataV1}`,
    }, context, 'updateJigMetadata', `getting jig ${payload.jigId}`);
  },

  loadTenantsLibrary(context: any): Promise<boolean> {
    const urlParams = {
      visibility: 'all',
    };
    const tenantId = getMyTenantIDFromContext(context);
    const url = `${AppConst.tenantsV1Url}/${tenantId}/library/v1`;

    return apiRequest({
      action: HTTPAction.GET,
      url: url,
      params: urlParams,
    }, context, 'updateModelsLibrary', `getting tenant library`,
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  loadLibraryEntry(context: any, payload: any): Promise<boolean> {
    const tenantId = getMyTenantIDFromContext(context);
    const url = AppConst.tenantsV1Url + `/${tenantId}/library/v1/${payload.Id}`;

    return apiRequest({
      action: HTTPAction.GET,
      url: url,
    }, context, 'updateLibraryEntry', `getting library entry details, Id: ${payload.Id}`,
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  loadLibraryCategories(context: any): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.libraryCategoriesUrl,
    }, context, 'updateLibraryCategories', `getting library categories`);
  },

  loadLibraryCategoryStates(context: any): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.libraryCategoryStatesUrl,
    }, context, 'updateLibraryCategoryStates', `getting library category states`);
  },

  deleteJigById(context: any, payload: any): Promise<boolean> {
    if (!payload.jigId || !payload.jigFormatVersion) {
      return Promise.reject('Need a jigId and jigFormatVersion');
    }
    var url = AppConst.myJigsUrl.concat('/', payload.jigId);
    if (payload.jigFormatVersion >= 4) { // use v2 if the format version is > 4
      url = AppConst.myJigsV2Url.concat('/', payload.jigId);
    }
    return apiRequest({
      action: HTTPAction.DELETE,
      url,
    }, context, 'deleteFromMyJigsById', `deleting jig id: ${payload.jigId}`);
  },

  deleteLibraryEntryById(context: any, id: string): Promise<boolean> {
    const tenantId = getMyTenantIDFromContext(context);
    const url = AppConst.tenantsV1Url + `/${tenantId}/library/v1/${id}`;
    return apiRequest({
      action: HTTPAction.DELETE,
      url: url,
    }, context, 'deleteFromModelsLibraryById', `deleting from models library, id: ${id}`,
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  deleteTenantById(context: any, tenantId: number): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.DELETE,
      url: AppConst.tenantsV1Url,
    },
    context,
    'deleteFromModelsLibraryById',
    `deleting organisation, id: ${tenantId}`,
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  copyJigById(context: any, payload: any): Promise<boolean> {
    const urlParams = {
      action: 'copy',
      name: payload.newName,
    };
    // Response object we get back on a 201 of the copy endpoint
    // MeJigV2CopyResponse is the structure returned when you copy a V2 Jig.
    // type MeJigV2CopyResponse struct {
    //     Status      string `json:"status"`
    //     HashId      string `json:"id"`
    //     Name        string `json:"name"`
    //     DeeplinkURL string `json:"deeplinkURL,omitempty"`
    //     Visibility  string `json:"visibility"`
    // }
    return apiRequest({
      action: HTTPAction.POST,
      url: `${AppConst.meJigsV2Url}/${payload.jigId}`,
      params: urlParams,
    },
    context, 'updateCopyJigResult', `copying jig ${payload.jigId} with new name ${payload.newName}`,
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: payload.tenantId,
    });
  },

  async updateJigMetadata(context: any, payload: JigMetaData, queryParams?: any): Promise<boolean> {
    if (!payload || !payload.Id || payload.Version == null) {
      consoleError(`Invalid updateJigMetadata payload. Required payload type: JigMetaData
      Received payload:`, payload);
      return new Promise(() => false);
    } else {
      const tenantId = getMyTenantIDFromContext(context);

      const requestQueryParam = queryParams ? {
        ...queryParams,
        version: payload.Version,
      } : { version: payload.Version }
      const results: any = {}

      results.isSuccess = await apiRequest(
        {
          action: HTTPAction.PUT,
          url: `${AppConst.meJigsV2Url}/${payload.Id}/${AppConst.jigMetadataV1}`,
          body: payload,
          params: requestQueryParam,
        },
        context,
        'updateSaveJigResult',
        `updating metadata of Jig ${payload.Id}`,
        AppConst.loadingMutationName,
        {
          [AppConst.CustomHeaderKeyTenantId]: tenantId,
        },
        (context: any, error: any, errorMsg: string) => {
          if (error.response) {
            results.status = error.response.status
          } else if (error.message.includes(`status code ${nonSystemErrors.unprocessableEntity.status}`) || error.message.includes('E040019')) {
            results.status = nonSystemErrors.unprocessableEntity.status
          }
        },
      );

      return results
    }
  },

  async updateJigVisibility(context: any, payload: JigVisibilities): Promise<boolean> {
    if (!payload || !payload.visibility || !payload.version == null) {
      consoleError(`Invalid updateJigVisibility payload. Required payload:
        JigVisibilities {
          jigId: string;
          visibility: string;
          regenPassword: boolean;
          version: number;
        }
      Received payload:`, payload);
      return new Promise(() => false);
    } else {
      const tenantId = getMyTenantIDFromContext(context);
      const results: any = {}

      results.isSuccess = await apiRequest(
        {
          action: HTTPAction.PUT,
          url: `${AppConst.myJigsV2Url}/${payload.jigId}/visibility/${payload.visibility}`,
          body: payload,
          params: {
            regenPassword: payload.regenPassword,
            version: payload.version
          },
        },
        context,
        'updateSaveJigVisibilityResult',
        `updating jig ${payload.jigId} visibility`,
        AppConst.loadingMutationName,
        {
          [AppConst.CustomHeaderKeyTenantId]: tenantId,
        },
        (context: any, error: any, errorMsg: string) => {
          if (error.response) {
            results.status = error.response.status
          } else if (error.message.includes(`status code ${nonSystemErrors.unprocessableEntity.status}`) || error.message.includes('E040019')) {
            results.status = nonSystemErrors.unprocessableEntity.status
          }
        },
      )

      return results
    }
  },

  updateLibraryEntry(context: any, libraryEntry: LibraryEntry): Promise<boolean> {
    if (!libraryEntry) {
      consoleError('Empty updateLibraryEntry libraryEntry received:', libraryEntry);
      return new Promise(() => false);
    }
    const tenantId = getMyTenantIDFromContext(context);
    if (tenantId !== libraryEntry.TenantId) {
      return Promise.reject('mismatch between library entry tenant id and currently selected Tenant ID');
    }
    const url = AppConst.tenantsV1Url + `/${libraryEntry.TenantId}/library/v1/${libraryEntry.Id}`;
    return apiRequest({
      action: HTTPAction.PUT,
      url: url,
      body: libraryEntry,
    }, context, '', `updating tenant library entry ${libraryEntry.Id}`,
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  upsertLibraryCategories(context: any, payload: LibraryCategory[]): Promise<boolean> {
    if (!payload) {
      consoleError('Empty upsertLibraryCategories payload (LibraryCategory[]) received:', payload);
      return new Promise(() => false);
    } else {
      return apiRequest({
        action: HTTPAction.PUT,
        url: AppConst.libraryCategoriesUrl,
        body: payload,
      }, context, 'updateLibraryCategories', `upserting library categories`);
    }
  },

  loadJigPermissions(context: any, payload: any): Promise<boolean> {
    const tenantId = getMyTenantIDFromContext(context);
    return apiRequest({
      action: HTTPAction.GET,
      url: `${AppConst.myJigsV2Url}/${payload.jigId}/permission`,
    },
    context,
    'updateJigPermissions',
    `getting jig permissions`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  createJigPermission(context: any, payload: JigPermissionCreateRequest): Promise<boolean> {
    const tenantId = getMyTenantIDFromContext(context);
    return apiRequest({
      action: HTTPAction.POST,
      url: `${AppConst.myJigsV2Url}/${payload.JigId}/permission`,
      params: payload.UrlParams,
      // body: payload.Perm,
    },
    context,
    'updateJigPermissions',
    `creating jig permission`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  deleteJigPermission(context: any, payload: JigPermissionDeleteRequest): Promise<boolean> {
    const tenantId = getMyTenantIDFromContext(context);
    return apiRequest({
      action: HTTPAction.DELETE,
      url: `${AppConst.myJigsV2Url}/${payload.ResID}/permission/${payload.PermID}`,
    },
    context,
    '',
    `deleting jig permission`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  updateJigPermission(context: any, payload: JigPermissionUpdateRequest): Promise<boolean> {
    const tenantId = getMyTenantIDFromContext(context);
    return apiRequest({
      action: HTTPAction.PUT,
      url: `${AppConst.myJigsV2Url}/${payload.Perm.ResID}/permission/${payload.Perm.ID}`,
      body: payload.Perm,
    },
    context,
    '', // We don't do anything with the response body as we need to request all permissions again.
    `updating jig permission`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenantId,
    });
  },

  loadMyTenants(context: any, payload: GetTenantsByUserRequest): Promise<boolean> {
    if (auth.uid === null || auth.uid <= 0) {
      return Promise.reject('invalid auth.uid');
    }
    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.usersV1Url + `/${auth.uid}/tenants/v1`,
      params: {
        ...payload.UrlParams,
      },
    },
    context,
    'updateMyTenants',
    `loading my organisation`,
    payload.ModalLoad ? AppConst.loadingModalMutationName : AppConst.loadingMutationName);
  },

  loadTeamJigsCount(context: any) {
    const urlParams = {
      op: 'count',
    };
    const tenantId = getMyTenantIDFromContext(context);

    return apiRequest(
      {
        action: HTTPAction.GET,
        url: `${AppConst.tenantsV1Url}/${tenantId}${AppConst.tenantsJigsUrl}`,
        params: urlParams,
      },
      context,
      'updateTenantUserCount',
      `getting TenantUserCount`,
      AppConst.loadingMutationName,
      {
        [AppConst.CustomHeaderKeyTenantId]: tenantId,
      }
    );
  },

  // overwrites the myTenants array with the tenants array
  updateMyTenantsWithNewTenant(context: any, tenant: Tenant) {
    const existingTenatnIndex = context.state.myTenants.findIndex((t: Tenant) => t.ID === tenant.ID)

    if (existingTenatnIndex !== -1) {
      // Use JSON.stringify and parse to deep copy value of the tenant object with ability to handle nested object. Object.assign cannot handle nested objects/references.
      context.state.myTenants[existingTenatnIndex] = AppHelper.deepCopyNestedJsonSafeObject(tenant)

      return
    }

    context.state.myTenants.push(AppHelper.deepCopyNestedJsonSafeObject(tenant))
    context.commit('updateMyTenants', context.state.myTenants)
  },

  updateMyMemberTenants(context: any, memberTenants: TenantToken[]) {
    context.commit('updateMyMemberTenants', memberTenants)
  },

  setMyTenant(context: any, payload: any) {
    context.commit('setMyTenantIndexById', payload.TenantId);
  },

  setMyTenantIndex(context: any, tenantIndex: number) {
    context.commit('setMyTenantIndex', tenantIndex);
  },

  setMyTenantLogoByID(context: any, payload: TenantLogoImageResponse) {
    if (payload.ID === undefined) {
      return Promise.reject('Undefined TenantId');
    }
    if (payload.ID <= 0) {
      return Promise.reject('Invalid TenantId');
    }
    context.commit('updateMyTenantLogoByID', payload);
  },

  setMyPreviousTenantIndex(context: any, tenantIndex: number) {
    context.commit('setMyPreviousTenantIndex', tenantIndex);
  },

  loadMyTenant(context: any, payload: any): Promise<boolean> {
    if (payload.TenantId === undefined) {
      return Promise.reject('Undefined TenantId');
    }
    if (payload.TenantId <= 0) {
      return Promise.reject('Invalid TenantId');
    }
    const params: any = {}
    if (payload.clearCache) {
      params.timestamp = (new Date()).valueOf()
    }
    return apiRequest({
      action: HTTPAction.GET,
      url: `${AppConst.tenantsV1Url}/${payload.TenantId}`,
      params,
    },
    context,
    'updateMyTenant',
    'loading my organisation',
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: payload.TenantId,
    });
  },
  loadMyTenantLogo(context: any, payload: Tenant): Promise<boolean> {
    if (context.state.myTenantIndex === TenantHelpers.InvalidTenantIndex) {
      return Promise.reject('Invalid Tenant Index');
    }
    const myTenant = context.state.myTenants[context.state.myTenantIndex] as Tenant;
    if (myTenant === null || myTenant.ID <= 0) {
      return Promise.reject('Invalid Tenant ID');
    }
    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.tenantsImagesV1Url,
    },
    context,
    'updateMyTenantLogo',
    'loading my organisation image',
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: myTenant.ID,
    });
  },
  loadMyTenantUsers(context: any): Promise<boolean> {
    if (context.state.myTenantIndex === TenantHelpers.InvalidTenantIndex) {
      return Promise.reject('Invalid Tenant Index');
    }
    const myTenant = context.state.myTenants[context.state.myTenantIndex] as Tenant;
    if (myTenant === null || myTenant.ID <= 0) {
      return Promise.reject('Invalid Tenant ID');
    }
    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.tenantsV1Url + '/users/v1',
    },
    context,
    'updateMyTenantUsers',
    'loading my organisation users',
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: myTenant.ID,
    });
  },
  loadTenantUserRoles(context: any, payload: any): Promise<boolean> {
    if (context.state.myTenantIndex === TenantHelpers.InvalidTenantIndex) {
      return Promise.reject('Invalid Tenant Index');
    }
    const myTenant = context.state.myTenants[context.state.myTenantIndex] as Tenant;
    if (myTenant === null || myTenant.ID <= 0) {
      return Promise.reject('Invalid Tenant ID');
    }
    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.tenantsV1Url + '/roles/v1',
    },
    context,
    'updateTenantUserRoles',
    'loading tenant user roles list',
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: myTenant.ID,
    });
  },
  loadTenantTotalJigCount(context: any): Promise<boolean> {
    if (context.state.myTenantIndex === TenantHelpers.InvalidTenantIndex) {
      return Promise.reject('Invalid Tenant Index');
    }
    const myTenant = context.state.myTenants[context.state.myTenantIndex] as Tenant;
    if (myTenant === null || myTenant.ID <= 0) {
      return Promise.reject('Invalid Tenant ID');
    }

    const requestUrl: string = `${AppConst.tenantsV1Url}/${myTenant.ID}${AppConst.tenantsJigsUrl}`

    const requestParams: any = {
      op: 'count',
      includeTenantPrivateJigs: true,
    }

    if (myTenant.Subscription.MaxJigsCountPer === 'user') {
      requestParams.filter = `uid,eq,${context.state.uid}`
    }

    return apiRequest({
      action: HTTPAction.GET,
      url: requestUrl,
      params: requestParams,
    },
    context,
    'updateTenantTotalJigCount',
    'loading tenant total Jig count',
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: myTenant.ID,
    });
  },
  setTenantLogo(context: any, payload: TenantLogoImageResponse) {
    if (payload.ID === undefined) {
      return Promise.reject('Undefined TenantId');
    }
    if (payload.ID <= 0) {
      return Promise.reject('Invalid TenantId');
    }
    context.commit('updateTenantLogo', payload);
  },
  async searchPaginatedTenants(context: any, payload?: any) {
    // context.commit('updateTenants', context.state.myTenants);
    const urlParams = payload ? {
      ...payload,
    } : {
      ...AppConst.defaultTenantsPagination,
    }

    if (payload && !payload.page) {
      urlParams.page = AppConst.defaultTenantsPagination.page
    }

    if (payload && !payload.limit) {
      urlParams.limit = AppConst.defaultTenantsPagination.limit
    }

    if (payload.name) {
      urlParams.name = payload.name
    } else {
      delete urlParams.name
    }

    if (payload.email) {
      urlParams.email = payload.email
    } else {
      delete urlParams.email
    }

    if (payload.sort) {
      urlParams.sort = payload.sort
    } else {
      delete urlParams.sort
    }

    return apiRequest({
      action: HTTPAction.GET,
      url: AppConst.tenantsV1Url,
      params: urlParams,
    },
    context,
    'updateTenants',
    'loading tenants search results',
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: context.state.myTenant.ID,
    });
  },
  loadTenantsVisibleToSuperUsers(context: any): Promise<boolean> {
    if (auth.uid === null || auth.uid <= 0) {
      return Promise.reject('invalid auth.uid');
    }
    // Note: This request is only successfully callable by staff users and will return 401 if not staff
    return apiRequest({
      action: HTTPAction.GET,
      url: `${AppConst.usersV1Url}/${auth.uid}/tenants/v1`,
      params: { all: 'true' },
    }, context, 'updateTenants', `loading organisation`);
  },
  createTenant(context: any, payload: TenantCreateRequest): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.POST,
      url: AppConst.tenantsV1Url,
      body: payload,
    },
    context,
    '',
    `creating organisation`,
    AppConst.loadingModalMutationName);
  },
  updateTenant(context: any, tenant: Tenant): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.PUT,
      url: AppConst.tenantsV1Url,
      body: tenant,
    },
    context,
    'updateTenant',
    `updating organisation`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenant.ID,
    });
  },
  updateMyTenant(context: any, tenant: Tenant): Promise<boolean> {
    return apiRequest({
      action: HTTPAction.PUT,
      url: AppConst.tenantsV1Url,
      body: tenant,
    },
    context,
    'updateMyTenant',
    `updating my organisation`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: tenant.ID,
    });
  },
  createCheckout(context: any, payload: CreateCheckoutRequest) {
    const msg = `creating checkout for user ${payload.UrlParams.email} and tenant ${payload.UrlParams.tenantId}`;
    if (payload.UrlParams.email.length === 0 ||
      payload.UrlParams.tenantId.length === 0 ||
      payload.UrlParams.successUrl.length === 0 ||
      payload.UrlParams.cancelUrl.length === 0) {
      return new Promise((resolve) => {
        onApiCatchError(context, Error(), `${msg}. Error: missing all parameters.`);
        resolve(false);
      });
    } else {
      return apiRequest({
        action: HTTPAction.GET,
        url: AppConst.createStripeCheckoutUrl,
        params: payload.UrlParams,
      },
      context,
      'updateCheckoutUrl',
      msg,
      AppConst.loadingModalMutationName,
      {
        [AppConst.CustomHeaderKeyTenantId]: payload.UrlParams.tenantId,
      });
    }
  },
  createCustomerPortal(context: any, payload: CreateCustomerPortalRequest) {
    const myTenant = context.state.myTenants[context.state.myTenantIndex] as Tenant;
    if (myTenant === null || myTenant.ID <= 0) {
      return Promise.reject('Invalid Tenant ID');
    }
    const msg = `creating customer portal for tenant ${myTenant.ID}`;
    if (payload.UrlParams.returnUrl.length === 0) {
      return new Promise((resolve) => {
        onApiCatchError(context, Error(), `${msg}. Error: missing all parameters.`);
        resolve(false);
      });
    } else {
      return apiRequest({
        action: HTTPAction.GET,
        url: AppConst.createStripeCustomerPortalUrl,
        params: payload.UrlParams,
      },
      context,
      'updateCustomerPortalUrl',
      msg,
      AppConst.loadingModalMutationName,
      {
        [AppConst.CustomHeaderKeyTenantId]: myTenant.ID,
      });
    }
  },
  addTenantUser(context: any, payload: TenantUserAddRequest) {
    return apiRequest({
      action: HTTPAction.POST,
      url: AppConst.tenantsV1Url + `/users/v1`,
      params: payload.UrlParams,
    },
    context,
    '',
    `adding tenant user`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: payload.TenantId,
    });
  },
  updateTenantUser(context: any, payload: TenantUserUpdateRequest) {
    if (!payload.UrlParams.role) {
      return Promise.reject('User role is empty.');
    }

    return apiRequest({
      action: HTTPAction.PUT,
      url: AppConst.tenantsV1Url + `/users/v1/${payload.UrlParams.Uid}`,
      params: payload.UrlParams,
    },
    context,
    '',
    `updating tenant user`,
    AppConst.loadingModalMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: payload.TenantId,
    });
  },
  removeTenantUser(context: any, payload: TenantUserRemoveRequest) {
    return apiRequest({
      action: HTTPAction.DELETE,
      url: AppConst.tenantsV1Url + `/users/v1/${payload.Uid}`,
    },
    context,
    '',
    `removing user`,
    AppConst.loadingMutationName,
    {
      [AppConst.CustomHeaderKeyTenantId]: payload.TenantId,
    });
  },

  clearJigShareLink(context: any, payload: JigShareURLData) {
    payload.requestInFlight = false;
    payload.shareURL = '';
    context.commit('setRequestingJigShareLink', payload);
  },

  setBranchIOError(context: any, error: any) {
    context.commit(AppConst.SetApiErrorMutationName, `Branch IO - something went wrong: ${error}.`, { root: true });
  },

  resetApiError(context: any) {
    context.commit(AppConst.SetApiErrorMutationName, '', { root: true });
  },

  async changeJigOwner(context: any, payload: ChangeJigOwnerRequest): Promise<{
    isSuccessful: boolean
    terminate: boolean
  }> {
    const msg = `changing jig owner. Jig ID: to ${payload.JigId}, email: ${payload.DesiredOwnerEmail}`;
    if (payload.JigId.length === 0 || payload.DesiredOwnerEmail.length === 0) {
      consoleError(`Invalid changeJigOwner payload. Required payload:
        ChangeJigOwnerRequest {
          JigId: string;
          DesiredOwnerEmail: string;
          DesiredOwnerTenantID?: number;
        }
      Received payload:`, payload);
      context.commit(AppConst.SetApiErrorMutationName, `Invalid changeJigOwner payload received.`, { root: true })
      return new Promise(() => ({
        isSuccessful: false,
        terminate: false,
      }))
    } else {
      const urlParams: any = {
        email: payload.DesiredOwnerEmail,
      }
      if (payload.action) {
        urlParams.action = payload.action
      }
      if (payload.DesiredOwnerTenantID) {
        urlParams.tenantID = payload.DesiredOwnerTenantID
      }

      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
        },
        params: urlParams,
      }

      try {
        const response: any = await Axios.put(`${AppConst.myJigsV2Url}/${payload.JigId}/owner`, {}, config)

        if (response.data) {
          context.commit('updateChangeJigOwnerResult', response.data as ChangeJigOwnerResult)

          return {
            isSuccessful: response.data.status === 'success',
            terminate: response.data.message !== `Resource not found: ${payload.JigId}`,
          }
        } else {
          consoleLog(`Jig ownership change failed, response was: ${JSON.stringify(response)}`)
          context.commit(AppConst.SetApiErrorMutationName, `Something went wrong when changing Jig ownership`, { root: true })
          return {
            isSuccessful: false,
            terminate: false,
          }
        }
      } catch (error: any) {
        consoleLog(`Jig ownership change failed, error: ${JSON.stringify(error.response)}`)
        const terminateAllRequests = error.response.data.message !== `Resource not found: ${payload.JigId}`

        if (terminateAllRequests) {
          context.commit(AppConst.SetApiErrorMutationName, `Error happened when changing Jig ownership. ${error.response.data.message}`, { root: true })
        }

        return {
          isSuccessful: false,
          terminate: terminateAllRequests,
        }
      }
    }
  },

  changeOwnerTenant(context: any, payload: ChangeOwnerTenantRequest): Promise<boolean> {
    const msg = `changing jig owner. Jig ID: to ${payload.JigId}, tenant ID: ${payload.DesiredOwnerTenantID}`;
    if (payload.JigId.length === 0 || payload.DesiredOwnerTenantID === TenantHelpers.InvalidTenantID) {
      consoleError(`Invalid changeOwnerTenant payload. Required payload:
        ChangeOwnerTenantRequest {
          JigId: string;
          DesiredOwnerTenantID: number;
        }
      Received payload:`, payload);
      return new Promise(() => false);
    } else {
      const urlParams = {
        tenantID: payload.DesiredOwnerTenantID,
      };
      return apiRequest({
        action: HTTPAction.PUT,
        url: `${AppConst.myJigsUrl}/v2/${payload.JigId}/owner`,
        params: urlParams,
      }, context, 'updateChangeJigOwnerResult', msg);
    }
  },

  async requestJigDeeplinkURL(context: any, payload: JigShareRequest): Promise<boolean> {
    if (payload == null) {
      return new Promise((reject) => reject(false));
    }

    return requestBranchJigLinkViaHttpApi(context, payload.jig, payload.branchIOCustomData, payload.targetApp);
  },

  // unused in favor of updateCustomerPaymentMethod
  async getCreditCardSetupIntent(context: any, payload: any): Promise<boolean> {
    if (payload.length === 0) {
      return Promise.reject('invalid getCreditCardSetupIntent, payload must be the customerEmail');
    } else {
      return apiRequest(
        {
          action: HTTPAction.POST,
          url: AppConst.billingUrl + '/stripe/v1/setup-intents',
          params: { customerEmail: payload },
        },
        context,
        'updateCreditCardSetupIntent',
        `getting setup intent`
      );
    }
  },

  async updateCustomerPaymentMethod(context: any, payload: any): Promise<boolean> {
    if (payload.customerEmail === undefined || payload.customerEmail.length === 0 ||
      payload.paymentMethodId === undefined || payload.paymentMethodId.length === 0) {
      return Promise.reject('invalid updateCustomerPaymentMethod, payload must have customerEmail and paymentMethodId');
    } else {
      return apiRequest(
        {
          action: HTTPAction.POST,
          url: AppConst.billingUrl + '/stripe/v1/customer-payment-method',
          params: { customerEmail: payload.customerEmail, paymentMethodId: payload.paymentMethodId },
        },
        context,
        'updateCustomerPaymentMethod',
        `updating customer payment method`,
        '',
        '',
        payload.onApiError,
      );
    }
  },

  // getSubscriptionWithInvoice requires the user to have billing_contact permissions for that tenant
  async getSubscriptionWithInvoice(context: any, payload: any): Promise<boolean> {
    if (payload.customerEmail === undefined || payload.customerEmail.length === 0 || payload.tenantId === undefined) {
      return Promise.reject('invalid getSubscription, payload must have customerEmail and tenantId');
    } else {
      return apiRequest(
        {
          action: HTTPAction.GET,
          url: AppConst.billingUrl + '/stripe/v1/subscription-with-invoice',
          params: { customerEmail: payload.customerEmail, tenantId: payload.tenantId },
        },
        context,
        'updateSubscription',
        `updating subscription`,
        '',
        {
          [AppConst.CustomHeaderKeyTenantId]: payload.tenantId,
        }
      );
    }
  },

  async getSubscription(context: any, payload: any): Promise<boolean> {
    if (payload.customerEmail === undefined || payload.customerEmail.length === 0 || payload.tenantId === undefined) {
      return Promise.reject('invalid getSubscription, payload must have customerEmail and tenantId');
    } else {
      return apiRequest(
        {
          action: HTTPAction.GET,
          url: AppConst.billingUrl + '/stripe/v1/subscription',
          params: { customerEmail: payload.customerEmail, tenantId: payload.tenantId },
        },
        context,
        'updateSubscription',
        `updating subscription`,
      );
    }
  },

  // setCancelSubscriptionAtEnd requires the user to have billing_contact permissions for that tenant
  async setCancelSubscriptionAtEnd(context: any, payload: any): Promise<boolean> {
    if (payload.stripeSubscriptionId === undefined || payload.stripeSubscriptionId.length === 0 ||
      payload.changedBy === undefined || payload.changedBy.length === 0 || payload.isCancelAtPeriodEnd === undefined || payload.tenantId === undefined) {
      return Promise.reject('invalid setCancelSubscriptionAtEnd, payload must have stripeSubscriptionId and changedBy and isCancelAtPeriodEnd and tenantId');
    } else {
      return apiRequest(
        {
          action: HTTPAction.PUT,
          url: AppConst.billingUrl + '/stripe/v1/subscription-cancel-at-period-end',
          body: { stripeSubscriptionId: payload.stripeSubscriptionId, changedBy: payload.changedBy, isCancelAtPeriodEnd: payload.isCancelAtPeriodEnd },
        },
        context,
        'updateSubscription',
        `updating subscription`,
        '',
        {
          [AppConst.CustomHeaderKeyTenantId]: payload.tenantId,
        }
      );
    }
  },

  async createTenantSetupJob(context: any, payload: any): Promise<boolean> {
    if (!payload.job || payload.job.customerEmail.length === 0 || payload.job.tenantToCreate === undefined || payload.job.tenantToCreate.Name.length === 0) {
      return Promise.reject('invalid TenantSetupJob. Needs a CustomerEmail, Tenant Name. A CustomerPassword is required if making a new user');
    } else {
      return apiRequest(
        {
          action: HTTPAction.POST,
          url: AppConst.tenantSetupJobsUrl,
          body: payload.job,
        },
        context,
        'updateTenantSetupJob',
        `creating Tenant Setup Job`,
        '',
        '',
        payload.onApiError,
      );
    }
  },

  async getTenantSetupJob(context: any, payload: any): Promise<boolean> {
    if (!payload || !payload.userEmail || payload.userEmail.length === 0) {
      return Promise.reject('invalid getTenantSetupJob, payload must be the userEmail');
    } else {
      return apiRequest(
        {
          action: HTTPAction.GET,
          url: AppConst.tenantSetupJobsUrl,
          params: { userEmail: payload.userEmail },
        },
        context,
        'updateTenantSetupJob',
        `getting Tenant Setup Job`,
        '',
        '',
        payload.onApiError,
      );
    }
  },

  async completeTenantSetupJob(context: any, payload: any): Promise<boolean> {
    return apiRequest(
      {
        action: HTTPAction.PUT,
        url: AppConst.completedTenantSetupJobsUrl,
      },
      context,
      'updateTenantSetupJob',
      `completing Tenant Setup Job`
    );
  },

  async sendEmailVerification(context: any, userEmailPayload: string): Promise<boolean> {
    if (!userEmailPayload || userEmailPayload.length === 0) {
      return Promise.reject('invalid sendEmailVerification, userEmailPayload must be the userEmail');
    } else {
      return apiRequest(
        {
          action: HTTPAction.PUT,
          url: AppConst.sendEmailVerificationUrl,
          params: { userEmail: userEmailPayload },
        },
        context,
        'updateSendEmailVerification',
        `sending Email Verification email`
      );
    }
  },

  forceRerenderCoreView(context: any, payload: any) {
    context.commit('setforceRerenderKey', payload);
  },

  uploadTenantLogoImage(context: any, payload: UploadTenantLogoRequest): Promise<boolean> {
    if (payload == null || payload.TenantId === TenantHelpers.InvalidTenantID || payload.Image.size === 0) {
      consoleError(`Invalid uploadTenantLogoImage payload. Required payload:
        UploadTenantLogoRequest {
          TenantId: number;
          Image: File;
        }
      Received payload:`, payload);
      return new Promise(() => false);
    } else {
      const file = new Blob([payload.Image], {
        type: 'application/octet-stream',
      });
      const formData = new FormData();
      formData.append('image', file);
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
          [AppConst.CustomHeaderKeyTenantId]: payload.TenantId,
        },
        timeout: 60000, // 1 min as milliseconds
      };
      const request = {
        action: HTTPAction.POST,
        url: AppConst.tenantsImagesV1Url,
        body: formData,
      };
      const requestPromise = apiRequestCustomConfig(
        request,
        context,
        '',
        `uploading new Tenant logo image. Tenant ID: ${payload.TenantId}, file: ${payload.Image.name}`,
        config,
        loadingModalMutationName);
      return requestPromise;
    }
  },

  async uploadJigThumbnail(context: any, payload: UploadThumbnailRequest): Promise<boolean> {
    if (payload == null || payload.Id.length === 0 || !payload.TenantId || payload.Thumbnail.size === 0 || payload.version == null) {
      consoleError(`Invalid uploadJigThumbnail payload. Required payload:
        UploadThumbnailRequest {
          Id: string;
          TenantId: number;
          Thumbnail: File;
          IsFirstThumbnail: boolean;
          version?: number;
          DateUpdated?: string;
        }
      Received payload:`, payload);
      return new Promise(() => false);
    } else {
      const file = new Blob([payload.Thumbnail], {
        type: 'application/octet-stream',
      });
      const formData = new FormData();
      // We change the thumbnail name before we send it as form data
      formData.append('image', file, `jig-${payload.Id}-color`);
      const urlParams = {
        cl: '6x6', // Compression Level Block Size (copied from Unity client)
        mode: 'medium', // Compression Speed (copied from Unity client)
        version: payload.version, // Jig's current version value in JigMetadata
      };
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
          [AppConst.CustomHeaderKeyTenantId]: payload.TenantId,
        },
        params: urlParams,
        timeout: 300 * 1000, // 5 minutes
      };
      const request = {
        action: payload.IsFirstThumbnail ? HTTPAction.POST : HTTPAction.PUT,
        url: `${AppConst.myJigsV2Url}/${payload.Id}/thumbnail`,
        body: formData,
      };
      const requestResults: any = {}
      requestResults.isSuccess = await apiRequestCustomConfig(
        request,
        context,
        'uploadThumbnailResult',
        `uploading new Jig thumbnail. Jig ID: ${payload.Id}, file: ${payload.Thumbnail.name}`,
        config,
        undefined,
        (context: any, error: any, errorMsg: string, onApiCatchError: any) => {
          if (error.response) {
            requestResults.status = error.response.status
            // When SUP error happens, we use a custom error message in UI, all other errors should use default API error catch event.
            if (requestResults.status !== nonSystemErrors.unprocessableEntity.status && requestResults.status !== nonSystemErrors.conflict.status) {
              onApiCatchError(context, error, errorMsg)
            }
          } else if (error.message.includes(`status code ${nonSystemErrors.unprocessableEntity.status}`) || error.message.includes('E040019')) {
            requestResults.status = nonSystemErrors.unprocessableEntity.status
          } else if (error.message.includes(`status code ${nonSystemErrors.conflict.status}`)) {
            requestResults.status = nonSystemErrors.conflict.status
          }
        }
      )
      return requestResults;
    }
  },
  // uploadLibraryEntryThumbnail can be awaited like any other action, but internally it takes a little longer
  // because our image compression is done through a seperate queue and service. We poll to update the result of
  // the compression after we upload the initial png.
  async uploadLibraryEntryThumbnail(context: any, payload: UploadThumbnailRequest): Promise<boolean> {
    if (payload == null || payload.Id.length === 0 || !payload.TenantId || payload.Thumbnail.size === 0) {
      consoleError(`Invalid uploadLibraryEntryThumbnail payload. Required payload:
        UploadThumbnailRequest {
          Id: string;
          TenantId: number;
          Thumbnail: File;
          IsFirstThumbnail: boolean;
        }
      Received payload:`, payload);
      return new Promise(() => false);
    } else {
      const file = new Blob([payload.Thumbnail], {
        type: 'application/octet-stream',
      });
      const formData = new FormData();
      // We change the thumbnail name before we send it as form data
      formData.append('thumbnail', file, `model-${payload.Id}.png`);
      const urlParams = {
        cl: '6x6', // Compression Level Block Size (copied from Unity client)
        mode: 'thorough', // Compression Speed (copied from Unity client)
      };
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
        },
        params: urlParams,
        timeout: 300 * 1000, // 5 minutes
      };
      const errorMsg = `uploading new library entry thumbnail. ID: ${payload.Id}, file: ${payload.Thumbnail.name}. `;
      try {
        return await Axios.post<QueueIdResult>(
          `${AppConst.publicModelsUrl}/${payload.Id}/thumbnail/offline`,
          formData, config).then(async (postImageResult) => {
          if (postImageResult.status === 200 && postImageResult.data.queueId !== '') {
            try {
              const queuedStatusResult = await pipeline.pollPipelineUntilDone(postImageResult.data.queueId, 20);
              context.commit('updateQueuedStatus', queuedStatusResult);
              context.commit(AppConst.loadingMutationName, false, { root: true });
              context.commit(AppConst.SetApiErrorMutationName, '', { root: true });
              return true;
            } catch (error) {
              onApiCatchError(context, Error(), errorMsg + (error as any).message);
              return false;
            }
          } else {
            onApiCatchError(context, Error(), errorMsg + `QueueId missing from response.`);
            return false;
          }
        });
      } catch (error) {
        onApiCatchError(context, error, errorMsg);
        return false;
      }
    }
  },

  async getDynaconfConfig(context: any): Promise<boolean> {
    const response: any = await Axios.get(AppConst.dynaconfConfig)

    if (response.data && response.data.pricing) {
      const pricingSets: { [key: string]: PricignListItem } = {}

      response.data.pricing.sets.forEach((set: any) => {
        pricingSets[set.name] = {
          name: set.name,
          tiers: {},
        }

        set.tiers.forEach((tier: PricingTier) => {
          pricingSets[set.name].tiers[tier.name] = tier
        })
      })

      context.commit('updateDynaconfConfig', {
        pricing: {
          sets: pricingSets,
        },
      } as DynaconfConfig)
      return true
    } else {
      consoleLog(`Dynoconf config retrieve failed, response was: ${JSON.stringify(response)}`)
      context.commit(AppConst.SetApiErrorMutationName, `Something went wrong getting dynaconf config.`, { root: true })
      return false
    }
  },
};

async function requestBranchJigLinkViaHttpApi(context: any, jig: MyJig|JigMetaData, requestData: any, targetApp: string): Promise<boolean> {
  if (context === null) {
    return new Promise((reject) => reject(false));
  }

  const jigDeeplinkURLRequesting = (jig as MyJig).deeplinkURLRequesting || (jig as JigMetaData).DeeplinkURLRequesting

  // Can't request the same share link at the same time.
  if (jigDeeplinkURLRequesting !== undefined && jigDeeplinkURLRequesting) {
    return new Promise((reject) => reject(false));
  }

  const deeplink = (jig as MyJig).deeplinkURL || (jig as JigMetaData).DeeplinkURL

  // We've already got a share url, return success
  if (deeplink !== undefined && deeplink.length > 0) {
    return new Promise((resolve) => resolve(true));
  }

  const jigID = (jig as MyJig).id || (jig as JigMetaData).Id

  context.commit('setRequestingJigShareLink', {
    jigId: jigID,
    requestInFlight: true,
    shareURL: '',
  } as JigShareURLData);

  const config: AxiosRequestConfig = {
    headers: {
      Authorization: `Bearer ${auth.accessToken}`,
    },
  };

  const reqBody = {
    ...requestData,
  };
  let response!: AxiosResponse<any>;

  const url = `${AppConst.jigsV1Url}/${jigID}/deeplinks/v1?target_app=${targetApp}`;
  response = await Axios.post(url, reqBody, config);
  if (response.data && response.data.url !== undefined && response.data.url.length > 0) {
    consoleLog(`Jig Share URL: ${response.data.url}`);
    context.commit('setRequestingJigShareLink', {
      jigId: jigID,
      requestInFlight: false,
      shareURL: response.data.url,
    } as JigShareURLData);
    return true;
  } else {
    consoleLog(`Jig Share URL was not successful, response was: ${JSON.stringify(response)}`);
    context.commit('setRequestingJigShareLink', {
      jigId: jigID,
      requestInFlight: false,
      shareURL: '',
    } as JigShareURLData);
    context.commit(AppConst.SetApiErrorMutationName, `Something went wrong getting a link for this Jig.`, { root: true });
    return false;
  }
}

function getMyTenantIDFromContext(context: any): number {
  return getMyTenantID(context.state);
}

export function getMyTenantID(appState: any): number {
  if (appState.myTenantIndex !== TenantHelpers.InvalidTenantIndex) {
    const myTenant = appState.myTenants[appState.myTenantIndex] as Tenant;
    if (myTenant !== null && myTenant.ID > 0) {
      return myTenant.ID;
    }
  }
  return TenantHelpers.InvalidTenantID;
}
